import {
  Box,
  Text,
  Tooltip,
  Button,
  useDisclosure,
  Skeleton,
} from '@chakra-ui/react';
import React from 'react';
import { useParams } from 'react-router-dom';

import { finleyAssert, finleyBuffer } from '@finley-technologies/common';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';
import { extractVirtualSheetConfiguration } from '@finley-technologies/tape-sanitizer';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { FileUploadButton } from '../calcAgentQA/FileUploadButton';
import { ConfigureParams } from './types';
import { FinleyAiModal } from 'packages/finleyAi/ui/FinleyAiModal';
import { convertErrorToStringMessages } from 'packages/tapeBuilder/ui/calcAgentQA/convertErrorToStringMessages';
import { Icon } from '~/packages/common-frontend/components/icons';

type Props = {
  onAutoConfigure: (configuration: ConfigureParams) => void;
  tapeBuilderNode: TapeBuilderUI.TapeBuilderNodeAbstract;
};
export const AutoConfigurePyXlsxVirtualSheet2CsvPanel = ({
  tapeBuilderNode,
  onAutoConfigure,
}: Props) => {
  const { configuration } = tapeBuilderNode;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: userData } = useGetUserQuery({});
  const params = useParams();
  const { organizationId } = params;
  finleyAssert(
    organizationId,
    'AutoConfigurePyXlsxVirtualSheet2CsvPanel:organizationId is required',
  );
  if (!userData || !userData.user) {
    return <Skeleton />;
  }

  if (
    !TapeBuilderUI.operationNodes.operation_pyXlsxVirtualSheet2Csv.isConfig(
      configuration,
    )
  ) {
    return (
      <Tooltip label="Invalid configuration schema">
        <Button type="button" isDisabled w="full">
          Configure
        </Button>
      </Tooltip>
    );
  }

  const { sheets, fields } = configuration;
  return (
    <>
      <Box mb="2">
        <Text mb="2">
          Generate config from XLSX tab &quot;finleyVirtualSheet&quot;
        </Text>
        <FileUploadButton
          allowedExtensions={['xlsx']}
          buttonText="Upload Tape"
          handleFile={async (arrayBuffer: ArrayBuffer) => {
            try {
              const virtualSheet = await extractVirtualSheetConfiguration({
                buffer: finleyBuffer.fromArrayBuffer(arrayBuffer),
              });
              onAutoConfigure({
                isValid: true,
                operation: 'pyXlsxVirtualSheet2Csv',
                configuration: {
                  fields,
                  sheets,
                  operation: 'pyXlsxVirtualSheet2Csv',
                  virtualSheet,
                },
              });
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error('Error generating configuration:', e);
              const errors = convertErrorToStringMessages(e);
              onAutoConfigure({
                isValid: false,
                operation: 'pyXlsxVirtualSheet2Csv',
                errors,
              });
            }
          }}
        />
      </Box>
      <Box mb="2">
        <Text mb="2">Generate config with finleyAi</Text>
        <Button
          rightIcon={<Icon icon="sparkles" />}
          variant="secondary"
          onClick={onOpen}
        >
          Generate config (WIP)
        </Button>
      </Box>
      <Box width="100%" display="flex" justifyContent="flex-end">
        {isOpen && (
          <FinleyAiModal
            isOpen={isOpen}
            onCancel={() => onClose()}
            targetOrganizationId={organizationId}
            type="virtualSheetConfig"
            userId={userData.user.id}
            userType={userData.user.userType}
          />
        )}
      </Box>
    </>
  );
};
